/**
 * Display helpers for the patients module. Pure; dates are rendered in the
 * practice's time zone so every workstation shows the same day.
 */
import type {
  HouseholdMemberView,
  HouseholdRelationship,
  Patient,
  PostalAddress,
} from '@velora/contracts/patients';
import { formatMoney } from '@velora/ui';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export function fullName(p: { firstName: string; lastName: string }): string {
  return `${p.firstName} ${p.lastName}`;
}

/** "Liam Carter" or, with a preferred name, still the legal name (preferred shown apart). */
export function listName(p: { firstName: string; lastName: string; preferredName: string | null }) {
  return `${p.lastName}, ${p.firstName}${p.preferredName ? ` (${p.preferredName})` : ''}`;
}

export function callName(p: { firstName: string; preferredName: string | null }): string {
  return p.preferredName && p.preferredName.trim() !== '' ? p.preferredName : p.firstName;
}

/** `1987-03-14` as `3/14/1987`. */
export function usDate(iso: string | null): string {
  if (!iso) return '';
  const [y, m, d] = iso.slice(0, 10).split('-');
  return `${Number(m)}/${Number(d)}/${y}`;
}

/** `2026-10-09` as `Oct 9` (or `Oct 9, 2025` in another year than `today`). */
export function shortDate(iso: string | null, today?: string): string {
  if (!iso) return '';
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number) as [number, number, number];
  const sameYear = today ? Number(today.slice(0, 4)) === y : true;
  return `${MONTHS[m - 1]} ${d}${sameYear ? '' : `, ${y}`}`;
}

/** Whole days from `today` to `iso` (negative in the past). */
export function daysFrom(today: string, iso: string): number {
  return Math.round(
    (Date.parse(`${iso.slice(0, 10)}T00:00:00Z`) - Date.parse(`${today}T00:00:00Z`)) / 86_400_000,
  );
}

export function relDays(today: string, iso: string): string {
  const d = daysFrom(today, iso);
  if (d === 0) return 'today';
  if (d === 1) return 'tomorrow';
  if (d === -1) return 'yesterday';
  if (d < 0) return -d < 45 ? `${-d} days ago` : `${Math.round(-d / 30)} mo ago`;
  return d < 45 ? `in ${d} days` : `in ${Math.round(d / 30)} mo`;
}

/** Local date (YYYY-MM-DD) of an instant in a time zone. */
export function zonedDate(instant: string | Date, timeZone: string): string {
  const d = typeof instant === 'string' ? new Date(instant) : instant;
  return new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(d);
}

export function zonedTime(instant: string | Date, timeZone: string): string {
  const d = typeof instant === 'string' ? new Date(instant) : instant;
  return new Intl.DateTimeFormat('en-US', { timeZone, hour: 'numeric', minute: '2-digit' }).format(
    d,
  );
}

export function zonedLong(instant: string, timeZone: string, today: string): string {
  const d = new Date(instant);
  const date = zonedDate(d, timeZone);
  const diff = daysFrom(today, date);
  if (diff === 0) return 'Today';
  if (diff === -1) return 'Yesterday';
  if (diff === 1) return 'Tomorrow';
  return new Intl.DateTimeFormat('en-US', {
    timeZone,
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    ...(date.slice(0, 4) !== today.slice(0, 4) ? { year: 'numeric' } : {}),
  }).format(d);
}

export const money = (cents: number) => formatMoney(cents, { showCents: cents % 100 !== 0 });
export const moneyExact = (cents: number) => formatMoney(cents, { showCents: true });

/** `+19895550123` as `(989) 555-0123`. */
export function phone(e164: string): string {
  const d = e164.replace(/\D/g, '');
  const n = d.length === 11 && d.startsWith('1') ? d.slice(1) : d;
  if (n.length !== 10) return e164;
  return `(${n.slice(0, 3)}) ${n.slice(3, 6)}-${n.slice(6)}`;
}

export function addressLine(a: PostalAddress): string {
  const city = [a.city, [a.state, a.postalCode].filter(Boolean).join(' ')]
    .filter(Boolean)
    .join(', ');
  return [a.line1, a.line2, city].filter(Boolean).join(', ');
}

export const GENDER_LABEL: Record<number, string> = {
  0: 'Male',
  1: 'Female',
  2: 'Unknown',
  3: 'Other',
};
export const GENDER_SHORT: Record<number, string> = { 0: 'M', 1: 'F', 2: 'U', 3: 'X' };
export const POSITION_LABEL: Record<number, string> = {
  0: 'Single',
  1: 'Married',
  2: 'Child',
  3: 'Widowed',
  4: 'Divorced',
};

export const STATUS_LABEL: Record<Patient['status'], string> = {
  active: 'Active',
  inactive: 'Inactive',
  archived: 'Archived',
  deceased: 'Deceased',
  non_patient: 'Guarantor only',
  prospective: 'New patient',
};

export const RELATIONSHIP_LABEL: Record<HouseholdRelationship, string> = {
  self: 'Guarantor',
  spouse: 'Spouse',
  child: 'Child',
  parent: 'Parent',
  sibling: 'Sibling',
  partner: 'Partner',
  other: 'Other',
};

const LANGS: Record<string, string> = {
  en: 'English',
  es: 'Spanish',
  fr: 'French',
  ar: 'Arabic',
  zh: 'Chinese',
  vi: 'Vietnamese',
  pl: 'Polish',
  de: 'German',
  hi: 'Hindi',
  ko: 'Korean',
  tl: 'Tagalog',
};
export function languageName(tag: string | null): string {
  if (!tag) return '';
  return LANGS[tag.split('-')[0] ?? ''] ?? tag;
}
export const LANGUAGE_OPTIONS = Object.entries(LANGS).map(([value, label]) => ({ value, label }));

/** Recall chip tone and label (prototype `recallChip`). */
export function recallState(
  today: string,
  due: string | null,
): { tone: 'danger' | 'warning' | 'brand' | 'neutral'; label: string } | null {
  if (!due) return null;
  const d = daysFrom(today, due);
  if (d < -30) return { tone: 'danger', label: `Overdue ${Math.max(1, Math.round(-d / 30))} mo` };
  if (d < 0) return { tone: 'warning', label: `Due ${-d}d ago` };
  if (d <= 14) return { tone: 'brand', label: d === 0 ? 'Due now' : `Due in ${d}d` };
  return { tone: 'neutral', label: `Due ${shortDate(due, today)}` };
}

export function memberName(
  m: Pick<HouseholdMemberView, 'firstName' | 'lastName' | 'preferredName'>,
) {
  return `${callName(m)} ${m.lastName}`;
}

/** Stable avatar tone from a name (prototype `avTone`). */
export function hashTone(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return Math.abs(h) % 8;
}

/** Compact day for tiles: Today, Tomorrow, a weekday within the week, else the date. */
export function visitDay(instant: string, timeZone: string, today: string): string {
  const date = zonedDate(instant, timeZone);
  const diff = daysFrom(today, date);
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Tomorrow';
  if (diff > 1 && diff < 7) {
    return new Intl.DateTimeFormat('en-US', { timeZone, weekday: 'long' }).format(
      new Date(instant),
    );
  }
  return shortDate(date, today);
}

/** "dixon" -> last Dixon; "jo dixon" -> first Jo, last Dixon; "dixon, jo" too. */
export function nameSeed(q: string): { first: string; last: string; display: string } {
  const cap = (w: string) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase();
  const t = q.trim();
  if (!t || /\d|@/.test(t)) return { first: '', last: '', display: t };
  if (t.includes(',')) {
    const [last = '', first = ''] = t.split(',').map((x) => x.trim());
    return { first: cap(first), last: cap(last), display: t };
  }
  const parts = t.split(/\s+/);
  if (parts.length === 1)
    return { first: '', last: cap(parts[0] ?? ''), display: cap(parts[0] ?? '') };
  const last = parts.pop() ?? '';
  return {
    first: parts.map(cap).join(' '),
    last: cap(last),
    display: `${parts.map(cap).join(' ')} ${cap(last)}`,
  };
}
